import { RuleConditions } from '~/consts/agentHub';

import type { Commission } from '~/types/services/agentHub';

import { additionalLocationToCommissionOption } from '~/utils/commission';

/** API / list shape for departure & arrival extras (often alongside flight rules). */
export type FlightLocationAdditionalInfo = {
  departure?: { city?: string; country?: string };
  arrival?: { city?: string; country?: string };
};

/**
 * Reads `additionalInfo` from a commission when present (contract types may lag the API).
 * Single place for the cast so components stay simple.
 */
export function readCommissionAdditionalInfo(commission: Commission | undefined): Record<string, unknown> | undefined {
  if (!commission || typeof commission !== 'object') {
    return undefined;
  }
  if (!('additionalInfo' in commission)) {
    return undefined;
  }
  const info = (commission as { additionalInfo?: unknown }).additionalInfo;
  if (info && typeof info === 'object' && !Array.isArray(info)) {
    return info as Record<string, unknown>;
  }
  return undefined;
}

export function getFlightLocationAdditionalInfo(
  commission: Commission | undefined,
): FlightLocationAdditionalInfo | undefined {
  const raw = readCommissionAdditionalInfo(commission);
  if (!raw) {
    return undefined;
  }
  const out: FlightLocationAdditionalInfo = {};
  if (raw.departure && typeof raw.departure === 'object' && !Array.isArray(raw.departure)) {
    out.departure = raw.departure as FlightLocationAdditionalInfo['departure'];
  }
  if (raw.arrival && typeof raw.arrival === 'object' && !Array.isArray(raw.arrival)) {
    out.arrival = raw.arrival as FlightLocationAdditionalInfo['arrival'];
  }
  return Object.keys(out).length ? out : undefined;
}

/**
 * Maps API rule keys to form `RuleConditions` keys (carrier → flightCarrier aliases, flight legs → departure/arrival).
 */
export function normalizeApiRulesForCommissionForm(rawRules: Record<string, unknown>): Record<string, unknown> {
  const next = { ...rawRules };
  if (next.carrier != null && next.flightCarrier == null) {
    next.flightCarrier = next.carrier;
  }
  if (next.flightDeparture != null && next.departure == null) {
    next.departure = next.flightDeparture;
  }
  if (next.flightArrival != null && next.arrival == null) {
    next.arrival = next.flightArrival;
  }
  delete next.carrier;
  delete next.flightDeparture;
  delete next.flightArrival;
  return next;
}

/**
 * After building `RuleConditions` from `rules`, overlay departure/arrival from `additionalInfo` when the API stores locations only there.
 */
export function overlayFlightLocationsFromAdditionalInfo(
  conditions: RuleConditions,
  additionalInfo: unknown,
): RuleConditions {
  if (!additionalInfo || typeof additionalInfo !== 'object' || Array.isArray(additionalInfo)) {
    return conditions;
  }
  const info = additionalInfo as FlightLocationAdditionalInfo;
  let next = conditions;
  if (info.departure && typeof info.departure === 'object') {
    const opt = additionalLocationToCommissionOption(info.departure);
    if (opt.length > 0) {
      next = { ...next, departure: opt };
    }
  }
  if (info.arrival && typeof info.arrival === 'object') {
    const opt = additionalLocationToCommissionOption(info.arrival);
    if (opt.length > 0) {
      next = { ...next, arrival: opt };
    }
  }
  return next;
}
